import axios from "axios";
import authService from "./authService";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

const API_BASE = `${BACKEND_URL}/storage-locations`;

/**
 * Storage Location Service
 * Gerencia todas as operações de locais de armazenamento
 */
class StorageLocationService {
  /**
   * Get authorization headers with JWT token
   * @returns {object} Headers object
   */
  getAuthHeaders() {
    const token = authService.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Get all storage locations
   * @returns {Promise<Array>}
   */
  async getAllStorageLocations() {
    try {
      const response = await axios.get(API_BASE, this.getAuthHeaders());
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar locais de armazenamento:", error);
      throw error;
    }
  }

  /**
   * Get storage location by ID
   * @param {number} id - Storage Location ID
   * @returns {Promise<object>}
   */
  async getStorageLocationById(id) {
    try {
      const response = await axios.get(
        `${API_BASE}/${id}`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao buscar local de armazenamento ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new storage location
   * @param {object} locationData - Storage location data
   * @returns {Promise<object>}
   */
  async createStorageLocation(locationData) {
    try {
      // Validar dados antes de enviar
      this.validateStorageLocationData(locationData);

      const response = await axios.post(
        API_BASE,
        locationData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao criar local de armazenamento:", error);
      throw error;
    }
  }

  /**
   * Update storage location
   * @param {number} id - Storage Location ID
   * @param {object} locationData - Updated storage location data
   * @returns {Promise<object>}
   */
  async updateStorageLocation(id, locationData) {
    try {
      // Validar dados antes de enviar
      this.validateStorageLocationData(locationData);

      const response = await axios.put(
        `${API_BASE}/${id}`,
        locationData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(
        `❌ Erro ao atualizar local de armazenamento ${id}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Delete storage location
   * @param {number} id - Storage Location ID
   * @returns {Promise<void>}
   */
  async deleteStorageLocation(id) {
    try {
      await axios.delete(`${API_BASE}/${id}`, this.getAuthHeaders());
    } catch (error) {
      console.error(`❌ Erro ao deletar local de armazenamento ${id}:`, error);
      throw error;
    }
  }

  /**
   * Validate storage location data
   * @param {object} data - Storage location data to validate
   * @throws {Error} If validation fails
   */
  validateStorageLocationData(data) {
    const errors = [];

    if (!data.name || data.name.trim() === "") {
      errors.push("Nome é obrigatório");
    }

    if (!data.equipment_id) {
      errors.push("Equipamento é obrigatório");
    }

    if (!data.capacity || data.capacity <= 0) {
      errors.push("Capacidade deve ser maior que zero");
    }

    if (errors.length > 0) {
      throw new Error(errors.join(", "));
    }
  }

  /**
   * Get available storage location types
   * @returns {Array<object>}
   */
  getStorageLocationTypes() {
    return [
      { value: "SHELF", label: "Prateleira", icon: "📚" },
      { value: "FREEZER", label: "Freezer", icon: "❄️" },
      { value: "REFRIGERATOR", label: "Geladeira", icon: "🧊" },
      { value: "CABINET", label: "Armário", icon: "🗄️" },
      { value: "WAREHOUSE", label: "Depósito", icon: "🏪" },
      { value: "DISPLAY", label: "Vitrine", icon: "🪟" },
      { value: "COUNTER", label: "Balcão", icon: "🍽️" },
      { value: "OTHER", label: "Outro", icon: "📦" },
    ];
  }

  /**
   * Get storage location type label
   * @param {string} type - Storage location type value
   * @returns {string}
   */
  getStorageLocationTypeLabel(type) {
    const types = this.getStorageLocationTypes();
    const found = types.find((t) => t.value === type);
    return found ? `${found.icon} ${found.label}` : type;
  }

  /**
   * Calculate usage percentage
   * @param {number} capacity - Total capacity
   * @param {number} usedCapacity - Used space
   * @returns {number}
   */
  calculateUsagePercentage(capacity, usedCapacity) {
    if (!capacity || capacity === 0) return 0;
    const used = usedCapacity || 0;
    return Math.round((used / capacity) * 100);
  }

  /**
   * Get usage status color
   * @param {number} usagePercentage - Usage percentage (0-100)
   * @returns {string}
   */
  getUsageStatusColor(usagePercentage) {
    if (usagePercentage >= 90) return "red";
    if (usagePercentage >= 70) return "yellow";
    return "green";
  }

  /**
   * Get usage status label
   * @param {number} usagePercentage - Usage percentage (0-100)
   * @returns {string}
   */
  getUsageStatusLabel(usagePercentage) {
    if (usagePercentage >= 90) return "Crítico";
    if (usagePercentage >= 70) return "Atenção";
    return "Normal";
  }
}

export default new StorageLocationService();
